import {Check, File as FileIcon, FolderNotchOpen} from '@phosphor-icons/react';
import {Directory as TDirectory, File as TFile, FileTree, scanFile} from 'Common/api';
import {useUserContext} from 'Contexts/UserContext/UserContext';
import React, {useCallback, useState} from 'react';
import {Project} from 'rendition-core';
import {twMerge} from 'tailwind-merge';

interface Props {
    tree: FileTree;
    project: Project;
}

interface FileProps {
    file: TFile;
    project: Project;
}

interface DirProps {
    directory: TDirectory;
    project: Project;
}

export const File: React.FC<FileProps> = ({file, project}) => {
    const {userId} = useUserContext();
    const [success, setSuccess] = useState(false);
    const doScanFile = useCallback(async () => {
        const {contents, path} = file;
        const repoId = project.github?.id!;

        const {success} = await scanFile({
            userId,
            repoId,
            content: atob(contents),
            path,
        });
        setSuccess(success);
    }, [file, project.github?.id, userId]);
    return (
        <div className="flex flex-row items-center gap-2">
            <FileIcon size={14} />
            <p className="mr-4 text-[14px] font-bold">{file.name}</p>
            <button
                className={twMerge(
                    'ml-auto flex w-12 items-center justify-center self-end rounded-xl border border-black px-2 py-0.5 text-[11px] font-semibold',
                    success && 'border-green-500 text-green-500'
                )}
                onClick={doScanFile}
            >
                {success ? <Check className="fill-green-500" size={11} /> : 'scan'}
            </button>
        </div>
    );
};

export const Directory: React.FC<DirProps> = ({directory, project}) => {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
                <FolderNotchOpen size={14} />
                <p className="text-[14px] font-bold">{directory.name}</p>
            </div>
            <div className="ml-3 flex flex-col gap-1">
                {directory.children.map((child) => {
                    if (child.type === 'file') {
                        return <File project={project} key={child.path} file={child} />;
                    }

                    return <Directory key={child.name} project={project} directory={child} />;
                })}
            </div>
        </div>
    );
};

export const ProjectFileTree: React.FC<Props> = ({tree, project}) => {
    return (
        <div className="flex flex-col gap-2">
            {tree.children.map((child) => {
                if (child.type === 'file') {
                    return <File project={project} key={child.path} file={child} />;
                }

                return <Directory key={child.name} project={project} directory={child} />;
            })}
        </div>
    );
};
